<template>
  <div class="onls-select__wrapper">
    <div v-if="label.length" class="field-label">{{ label }}</div>

    <vue-select
        :class="['onls-select', {'onls-select__selected': String(value).length }]"
        :label="itemText"
        :options="options"
        v-model="modelValue"
        :reduce="onReduce"
        :placeholder="placeholder"
        @update:model-value="onInput"
    />

    <onls-field-error :error-message="errorMessage"/>
  </div>
</template>

<script setup lang="ts">
import VueSelect from "vue-select";
import {computed, type PropType} from "vue";
import { OnlsFieldError } from "../index";

const props = defineProps({
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  reduce: { type: String, default: null },
  value: [String, Number],
  options: Array as PropType<Array<Record<string, any>>>,
  itemText: { type: String, default: 'text' },
  errorMessage: { type: String, default: "" },
});

const emits = defineEmits(['update:value']);

const modelValue = computed({
  get() {
    return props.value;
  },
  set: (option: any) => onInput(option),
})

const onReduce = (item: any) => props.reduce ? item[props.reduce] : item

const onInput = (option: Record<string, any>) => {
  emits('update:value', (props.reduce && !!option && option[props.reduce]) ? option[props.reduce] : option);
}
</script>

<style lang="scss">
@import "vue-select/dist/vue-select.css";

.onls-select {
  &__wrapper {
    width: 100%;
  }

  .vs {
    &__dropdown-toggle {
      border: 2px solid var(--secondary-text);
      border-radius: 5px;
      padding: 15px 20px;
      cursor: pointer;
    }

    &__selected-options {
      min-height: 20px;
      padding: 0;
      display: flex;
      align-items: center;
    }

    &__search,
    &__selected
    {
      font-size: 16px;
      line-height: 16px;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
    }

    &__search {
      color: var(--secondary-text) !important;
    }

    &__clear {
      &:hover {
        fill: var(--primary-text);
      }
    }

    &__open-indicator {
      cursor: pointer;
      fill: var(--secondary-text);

      &:hover {
        fill: var(--primary-text);
      }
    }

    &__dropdown-menu {
      background: var(--primary-background);
      border: 2px solid var(--secondary-text);
      border-top: none;
      border-radius: 5px;

      transition: none;
    }

    &__dropdown-option {
      padding: 15px;
      color: var(--primary-text);

      &:hover {
        background: var(--secondary-background);
      }
    }

    &__dropdown-option--highlight {
      background: inherit;
      color: var(--primary-text);
    }
  }

  &:hover {
      .vs {
        &__dropdown-toggle {
          border: 2px solid var(--primary-text);
        }

        &__open-indicator {
          fill: var(--primary-text);
        }
      }
  }

  &__selected {
    .vs {
      &__dropdown-toggle {
        border: 2px solid var(--primary-text);
      }

      &__open-indicator {
        fill: var(--primary-text);
      }

      &__clear {
        fill: var(--primary-text);
      }

      &__selected {
        color: var(--primary-text);
      }
    }
  }
}

.vs--open {
  .vs {
    &__open-indicator {
      fill: var(--primary-text);
    }
  }

  .vs__dropdown-toggle {
    border: 2px solid var(--primary-text);
    border-bottom-color: transparent !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .vs__dropdown-menu {
    box-shadow: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border: 2px solid var(--primary-text);
    border-top: none;
  }

  .vs__no-options {
    padding: 15px;
  }
}
</style>
